import React, { useState } from "react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Badge } from "../../../../components/ui/badge";
import { ChevronDown, ChevronRight } from "lucide-react";
import type { TreinamentosDesempenho } from "../../../../types/desempenho";

interface TreinamentosTabProps {
  data: TreinamentosDesempenho | null;
}

const formatDateBR = (dateStr: string) => {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('pt-BR');
};

const CustomChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload?.length) {
    return (
      <div className="bg-card border border-border rounded-lg px-3 py-2 text-sm shadow-md">
        <p className="font-medium text-foreground">{label}</p>
        {payload.map((entry: any, idx: number) => (
          <p key={idx} className="text-muted-foreground">
            {entry.name}: <span className="font-bold" style={{ color: entry.color }}>{Number(entry.value).toLocaleString('pt-BR')}</span>
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export function TreinamentosTab({ data }: TreinamentosTabProps) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [filtroGraficoDiario, setFiltroGraficoDiario] = useState<string>("todos");
  const [filtroGraficoHorario, setFiltroGraficoHorario] = useState<string>("todos");

  if (!data) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          Nenhum dado disponível. Use os filtros acima para buscar informações.
        </CardContent>
      </Card>
    );
  }

  // Obter lista de todos os treinamentos únicos
  const todosOsTreinamentos = React.useMemo(() => {
    const treinamentosSet = new Set<string>();
    data.treinadores.forEach(t => {
      Object.keys(t.porTipo || {}).forEach(nome => treinamentosSet.add(nome));
    });
    return Array.from(treinamentosSet).sort();
  }, [data.treinadores]);

  const toggleRow = (id: string) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  // Preparar dados para gráfico de volumetria por dia (filtrado)
  const volumetriaDiariaData = React.useMemo(() => {
    if (filtroGraficoDiario === "todos") {
      return data.volumetria.porDia.map(item => ({
        data: formatDateBR(item.data),
        total: item.total,
        aprovados: item.aprovados,
        reprovados: item.reprovados,
      }));
    }

    // Agregar dados por dia filtrando por treinamento específico
    const dadosPorDia = new Map<string, { rawDate: string; total: number; aprovados: number; reprovados: number }>();

    data.treinadores.forEach(treinador => {
      treinador.porDia?.forEach(dia => {
        const dataStr = formatDateBR(dia.data);
        const dadosTipo = dia.porTipo?.[filtroGraficoDiario];

        if (dadosTipo) {
          if (!dadosPorDia.has(dataStr)) {
            dadosPorDia.set(dataStr, { rawDate: dia.data, total: 0, aprovados: 0, reprovados: 0 });
          }
          const atual = dadosPorDia.get(dataStr)!;
          atual.total += dadosTipo.aplicados;
          atual.aprovados += dadosTipo.aprovados;
          atual.reprovados += dadosTipo.reprovados;
        }
      });
    });

    return Array.from(dadosPorDia.entries())
      .map(([dataStr, valores]) => ({
        data: dataStr,
        total: valores.total,
        aprovados: valores.aprovados,
        reprovados: valores.reprovados,
        rawDate: valores.rawDate,
      }))
      .sort((a, b) => a.rawDate.localeCompare(b.rawDate));
  }, [data, filtroGraficoDiario]);

  // Preparar dados para gráfico de volumetria por hora (filtrado)
  const volumetriaHorariaData = React.useMemo(() => {
    if (filtroGraficoHorario === "todos") {
      return data.volumetria.porHora.map(item => ({
        hora: `${item.hora}h`,
        total: item.total,
        aprovados: item.aprovados,
        reprovados: item.reprovados,
      }));
    }
    return data.volumetria.porHora.map(item => {
      const tipo = item.porTipo?.[filtroGraficoHorario];
      return {
        hora: `${item.hora}h`,
        total: tipo?.total ?? 0,
        aprovados: tipo?.aprovados ?? 0,
        reprovados: tipo?.reprovados ?? 0,
      };
    }).filter(item => item.total > 0 || data.volumetria.porHora.some(
      h => h.porTipo?.[filtroGraficoHorario] !== undefined
    ));
  }, [data, filtroGraficoHorario]);

  return (
    <div className="space-y-6">
      {/* Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Treinamentos</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.resumo.totalTreinamentos}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Treinadores</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data.resumo.totalTreinadores}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Treinados</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {data.volumetria.porDia.reduce((acc, dia) => acc + dia.total, 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Aprovados</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {data.volumetria.porDia.reduce((acc, dia) => acc + dia.aprovados, 0)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Reprovados</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {data.volumetria.porDia.reduce((acc, dia) => acc + dia.reprovados, 0)}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gráfico de Evolução de Treinamentos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle>Evolução Diária</CardTitle>
              <CardDescription>Treinamentos por dia</CardDescription>
            </div>
            <select
              value={filtroGraficoDiario}
              onChange={(e) => setFiltroGraficoDiario(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="todos">Todos os Treinamentos</option>
              {todosOsTreinamentos.map(treinamento => (
                <option key={treinamento} value={treinamento}>{treinamento}</option>
              ))}
            </select>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={volumetriaDiariaData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="currentColor" strokeOpacity={0.06} />
                <XAxis dataKey="data" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="total" stroke="#8884d8" name="Total" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="aprovados" stroke="#22c55e" name="Aprovados" strokeWidth={2.5} dot={false} />
                <Line type="monotone" dataKey="reprovados" stroke="#ef4444" name="Reprovados" strokeWidth={2.5} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <div>
              <CardTitle>Distribuição por Horário</CardTitle>
              <CardDescription>Treinamentos por hora do dia</CardDescription>
            </div>
            <select
              value={filtroGraficoHorario}
              onChange={(e) => setFiltroGraficoHorario(e.target.value)}
              className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              <option value="todos">Todos os Treinamentos</option>
              {todosOsTreinamentos.map(treinamento => (
                <option key={treinamento} value={treinamento}>{treinamento}</option>
              ))}
            </select>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={volumetriaHorariaData} margin={{ top: 8, right: 16, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="4 4" stroke="currentColor" strokeOpacity={0.06} />
                <XAxis dataKey="hora" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                <Tooltip content={<CustomChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="total" fill="#8884d8" name="Total" radius={[4, 4, 0, 0]} />
                <Bar dataKey="aprovados" fill="#22c55e" name="Aprovados" radius={[4, 4, 0, 0]} />
                <Bar dataKey="reprovados" fill="#ef4444" name="Reprovados" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Top Treinadores - Tabela Expandível */}
      <Card>
        <CardHeader>
          <CardTitle>Top Treinadores</CardTitle>
          <CardDescription>Desempenho detalhado por tipo de treinamento</CardDescription>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-2 text-left font-medium" rowSpan={2}>Nick</th>
                {todosOsTreinamentos.map(treinamento => (
                  <th key={treinamento} className="p-2 text-center font-medium border-l" colSpan={4}>
                    {treinamento}
                  </th>
                ))}
                <th className="p-2 text-center font-medium border-l" colSpan={4}>Total</th>
                <th className="p-2" rowSpan={2}></th>
              </tr>
              <tr className="border-b text-xs text-muted-foreground">
                {todosOsTreinamentos.map(treinamento => (
                  <React.Fragment key={`sub-${treinamento}`}>
                    <th className="p-1 text-center border-l">Aplic.</th>
                    <th className="p-1 text-center">Trein.</th>
                    <th className="p-1 text-center">Aprov.</th>
                    <th className="p-1 text-center">Reprov.</th>
                  </React.Fragment>
                ))}
                <th className="p-1 text-center border-l">Aplic.</th>
                <th className="p-1 text-center">Trein.</th>
                <th className="p-1 text-center">Aprov.</th>
                <th className="p-1 text-center">Reprov.</th>
              </tr>
            </thead>
            <tbody>
              {[...data.treinadores]
                .sort((a, b) => (b.total?.aplicados || 0) - (a.total?.aplicados || 0))
                .slice(0, 20).map((treinador) => {
                const isExpanded = expandedRows.has(treinador.militar.id);
                return (
                  <React.Fragment key={treinador.militar.id}>
                    {/* Linha principal */}
                    <tr className="border-b hover:bg-secondary/30">
                      <td className="p-2">
                        <div className="font-medium">{treinador.militar.nome}</div>
                        <div className="text-xs text-muted-foreground">{treinador.militar.patente}</div>
                      </td>
                      {todosOsTreinamentos.map(treinamento => {
                        const dados = treinador.porTipo?.[treinamento] || { aplicados: 0, treinados: 0, aprovados: 0, reprovados: 0 };
                        return (
                          <React.Fragment key={`${treinador.militar.id}-${treinamento}`}>
                            <td className="p-1 text-center border-l">{dados.aplicados}</td>
                            <td className="p-1 text-center">{dados.treinados}</td>
                            <td className="p-1 text-center text-green-600">{dados.aprovados}</td>
                            <td className="p-1 text-center text-red-600">{dados.reprovados}</td>
                          </React.Fragment>
                        );
                      })}
                      <td className="p-1 text-center border-l font-bold">{treinador.total?.aplicados || 0}</td>
                      <td className="p-1 text-center font-bold">{treinador.total?.treinados || 0}</td>
                      <td className="p-1 text-center font-bold text-green-600">{treinador.total?.aprovados || 0}</td>
                      <td className="p-1 text-center font-bold text-red-600">{treinador.total?.reprovados || 0}</td>
                      <td className="p-1 text-center">
                        <button
                          onClick={() => toggleRow(treinador.militar.id)}
                          className="p-1 hover:bg-secondary rounded"
                        >
                          {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                        </button>
                      </td>
                    </tr>

                    {/* Linhas expandidas - dados por dia */}
                    {isExpanded && treinador.porDia && treinador.porDia.length > 0 && (
                      <>
                        {/* Header do detalhe por dia */}
                        <tr className="bg-secondary/10">
                          <td className="p-2 text-sm font-medium">
                            Dia
                          </td>
                          {todosOsTreinamentos.map(treinamento => (
                            <React.Fragment key={`dia-sub-exp-${treinamento}`}>
                              <td className="p-1 text-center text-xs text-muted-foreground border-l">Aplic.</td>
                              <td className="p-1 text-center text-xs text-muted-foreground">Trein.</td>
                              <td className="p-1 text-center text-xs text-muted-foreground">Aprov.</td>
                              <td className="p-1 text-center text-xs text-muted-foreground">Reprov.</td>
                            </React.Fragment>
                          ))}
                          <td className="p-1 text-center text-xs text-muted-foreground border-l">Aplic.</td>
                          <td className="p-1 text-center text-xs text-muted-foreground">Trein.</td>
                          <td className="p-1 text-center text-xs text-muted-foreground">Aprov.</td>
                          <td className="p-1 text-center text-xs text-muted-foreground">Reprov.</td>
                          <td></td>
                        </tr>
                        {treinador.porDia.map((dia, idx) => (
                          <tr key={`${treinador.militar.id}-dia-${idx}`} className="bg-secondary/10 border-b text-xs">
                            <td className="p-2 pl-4">
                              {formatDateBR(dia.data)}
                            </td>
                            {todosOsTreinamentos.map(treinamento => {
                              const dadosDia = dia.porTipo?.[treinamento] || { aplicados: 0, treinados: 0, aprovados: 0, reprovados: 0 };
                              return (
                                <React.Fragment key={`${treinador.militar.id}-${idx}-${treinamento}`}>
                                  <td className="p-1 text-center border-l">{dadosDia.aplicados}</td>
                                  <td className="p-1 text-center">{dadosDia.treinados}</td>
                                  <td className="p-1 text-center text-green-600">{dadosDia.aprovados}</td>
                                  <td className="p-1 text-center text-red-600">{dadosDia.reprovados}</td>
                                </React.Fragment>
                              );
                            })}
                            <td className="p-1 text-center border-l font-medium">{dia.total?.aplicados || 0}</td>
                            <td className="p-1 text-center font-medium">{dia.total?.treinados || 0}</td>
                            <td className="p-1 text-center font-medium text-green-600">{dia.total?.aprovados || 0}</td>
                            <td className="p-1 text-center font-medium text-red-600">{dia.total?.reprovados || 0}</td>
                            <td></td>
                          </tr>
                        ))}
                      </>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Top Aprovadores */}
      <Card>
        <CardHeader>
          <CardTitle>Top Aprovadores</CardTitle>
          <CardDescription>Aprovadores com mais relatórios aceitos</CardDescription>
        </CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground text-xs">
                <th className="text-left pb-2 w-8">#</th>
                <th className="text-left pb-2">Militar</th>
                <th className="text-left pb-2">Patente</th>
                <th className="text-right pb-2">Aceitos</th>
              </tr>
            </thead>
            <tbody>
              {data.aprovadores.slice(0, 10).map((aprovador, index) => (
                <tr key={aprovador.militar.id} className="border-b border-border/50 last:border-0 hover:bg-secondary/20 transition-colors">
                  <td className="py-2 text-muted-foreground text-xs">{index + 1}</td>
                  <td className="py-2 font-medium">{aprovador.militar.nome}</td>
                  <td className="py-2 text-muted-foreground">{aprovador.militar.patente}</td>
                  <td className="py-2 text-right font-bold">{aprovador.relatoriosAceitos}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
